import { Injectable } from "@angular/core";
import {
  BigNumber,
  CurrencyPair,
  CurrencyPairImpl,
  OrderDataImpl,
  OrderIntent,
  Product,
  RateData,
  RequestOrderType,
} from "shared/cubiq-model-multi-asset";
import { DealtPricingService } from "shared/cubiq-services/lookup/pricing/impl/DealtPricingService";
import { EMPTY_RATE_STRING } from "shared/cubiq-services/lookup/pricing/PricingService";

import { LoggerService } from "@bfx/logger";
import { CcyPairLookupService } from "../../lookup/model/impl/CcyPairLookupService";
import { SysFeatures, SystemConfigPromiseService } from "../../../auth-services";
const logger = LoggerService.for(__dirname);

const ONE: BigNumber = new BigNumber("1");
const ZERO: BigNumber = new BigNumber("0");

const SL_REQUESTED_ORDER_TYPES: RequestOrderType[] = [
  RequestOrderType.SL_ATRATE,
  RequestOrderType.SL_BID,
  RequestOrderType.SL_ASK,
  RequestOrderType.TSL,
  RequestOrderType.TSL_BID,
  RequestOrderType.TSL_ASK,
];

@Injectable()
export class OrderSlippageService {
  private autoApplySlippage: boolean = false;
  private slippageEnabled: boolean = false;

  constructor(
    protected pricingService: DealtPricingService,
    protected currencyPairLookup: CcyPairLookupService,
    systemConfiguration: SystemConfigPromiseService,
  ) {
    systemConfiguration.getSystemConfig(SysFeatures.auto_sl_slippage).then((value: string) => {
      this.autoApplySlippage = value == "Y";
      logger.out.info(
        `${this.constructor.name}: Auto SL Slippage is ${this.autoApplySlippage ? "on" : "off"}`,
      );
    });
  }

  /**
   * Applies the slippage as configured by the AutoSLSlippage system flag and the auto slippage value on
   * currency pair. The slippage calculated is than applied to the rate data supplied as
   * the fillData argument.
   *
   * @param orderData the order data for the order being filled
   * @param fillData the fill data as obtained from lock for fill
   * @returns the slippage applied in decimal form, null for order types that don't support slippage, 0 for SL/TSL type
   */
  /* Unused in 4.22 */
  public applySlippage(orderData: OrderDataImpl, fillData: RateData): BigNumber {
    let slippage: BigNumber = null;

    if (
      orderData != null &&
      this.autoApplySlippage &&
      this.slippageEnabled &&
      SL_REQUESTED_ORDER_TYPES.includes(orderData.requestedOrderType)
    ) {
      let orderId: string = orderData.orderId != null ? orderData.orderId.toString() : null;
      let currencyPair: CurrencyPair = this.currencyPairLookup.getCcyPair(
        orderData.baseCode,
        orderData.nonBaseCode,
      );

      slippage = currencyPair.slSlippagePips;

      if (slippage == null || slippage.comparedTo(0) == 0) {
        logger.out.info(
          `${this.constructor.name}: Slippage for pair [${orderData.baseCode}, ${
            orderData.nonBaseCode
          }] is (${slippage != null ? slippage.toFormat() : null}. Skipping application.)`,
        );
      } else {
        try {
          let pipPrecision: number = currencyPair.rateDecimalsQty;
          let pipSize = new BigNumber("10").pow(-pipPrecision);
          slippage = pipSize.times(slippage);

          if (orderData.isInvertedOrderFg === true) {
            logger.out.debug(
              `${
                this.constructor.name
              }: Order ${orderData.orderId.toString()} is an SL inverted order. Calculating slippage ...`,
            );
            let trackingRateString: string = this.pricingService.getDealtFwdTracking(orderData);

            if (trackingRateString != null && trackingRateString != EMPTY_RATE_STRING) {
              // Dealt, so inverted if the order is invert
              let trackingRate: BigNumber = new BigNumber(trackingRateString);

              if (this.getClientBuysBase(orderData)) {
                slippage = ONE.dividedBy(ONE.dividedBy(trackingRate).minus(slippage)).minus(
                  trackingRate,
                );
              } else {
                slippage = trackingRate.minus(
                  ONE.dividedBy(ONE.dividedBy(trackingRate).plus(slippage)),
                );
              }

              pipPrecision = currencyPair.spotInvertDecimalsQty;

              slippage = slippage.decimalPlaces(pipPrecision + 1);
            }
          }

          logger.out.info(
            `${
              this.constructor.name
            }: Applying slippage of ${slippage.toFormat()} to order ${orderId} ...`,
          );

          this.applySlippageToRateData(slippage, orderData, currencyPair, fillData);
        } catch (e) {
          logger.out.error(
            `${this.constructor.name}: Error while calculating slippage for order ${orderId}, slippage will not be auto applied: ${e}`,
          );
        }
      }
    }

    if (slippage == null && SL_REQUESTED_ORDER_TYPES.includes(orderData.requestedOrderType)) {
      slippage = ZERO;
    }

    return slippage;
  }

  /**
   * Gets the autoslippage for given currency if both slipage and auto slipage is enabled
   * @param pair the pair
   * @returns the auto slipage value in decimal format if auto slipage and slipage are enabled
   * and present on the pair, otherwise returns null
   */
  public getAutoSlippage(pair: string): BigNumber {
    let autoSlippage: BigNumber = null;

    if (this.slippageEnabled && this.autoApplySlippage && pair.length === 6) {
      let baseCode: string = pair.substring(0, 3);
      let nonBase: string = pair.substring(3);

      let currencyPair: CurrencyPair = this.currencyPairLookup.getCcyPair(baseCode, nonBase);

      let slippage = currencyPair.slSlippagePips;

      if (slippage != null && slippage.comparedTo(0) != 0) {
        autoSlippage = slippage;
      }
    }

    return autoSlippage;
  }

  /**
   * Will calculate slippage for a given order if the autoslippage and slippage are enabled.
   *
   * @param orderData the order
   * @returns slippage if enabled and autoslippage is enabled on the system or null otherwise
   */
  /* Unused in 4.22 */
  public getSlippage(orderData: OrderDataImpl): BigNumber {
    let slippage: BigNumber = null;

    if (
      orderData != null &&
      this.autoApplySlippage &&
      this.slippageEnabled &&
      SL_REQUESTED_ORDER_TYPES.includes(orderData.requestedOrderType)
    ) {
      let orderId: string = orderData.orderId != null ? orderData.orderId.toString() : null;
      let currencyPair: CurrencyPair = this.currencyPairLookup.getCcyPair(
        orderData.baseCode,
        orderData.nonBaseCode,
      );

      slippage = currencyPair.slSlippagePips;

      if (slippage != null && slippage.comparedTo(0) != 0) {
        try {
          let pipPrecision: number = currencyPair.rateDecimalsQty;
          let pipSize = new BigNumber("10").pow(-pipPrecision);
          slippage = pipSize.times(slippage);

          if (orderData.isInvertedOrderFg === true) {
            let trackingRateString: string = this.pricingService.getDealtFwdTracking(orderData);

            if (trackingRateString != null && trackingRateString != EMPTY_RATE_STRING) {
              // Dealt, so inverted if the order is invert
              let trackingRate: BigNumber = new BigNumber(trackingRateString);

              if (this.getClientBuysBase(orderData)) {
                slippage = ONE.dividedBy(ONE.dividedBy(trackingRate).minus(slippage)).minus(
                  trackingRate,
                );
              } else {
                slippage = trackingRate.minus(
                  ONE.dividedBy(ONE.dividedBy(trackingRate).plus(slippage)),
                );
              }

              pipPrecision = currencyPair.spotInvertDecimalsQty;

              slippage = slippage.decimalPlaces(pipPrecision + 1);
            }
          }

          slippage = new BigNumber("10").pow(pipPrecision).times(slippage);

          logger.out.info(
            `${
              this.constructor.name
            }: Calculated slippage of ${slippage.toFormat()} for order ${orderId} ...`,
          );
        } catch (e) {
          logger.out.error(
            `${this.constructor.name}: Error while calculating slippage for order ${orderId}: ${e}`,
          );
        }
      }
    }

    return slippage;
  }

  /**
   * Applies slippage to rate data object as obtained in a lock for fill response
   * @param slippage the slippage to be applied
   * @param order the order data for the order being filled
   * @param pair the currency pair object
   * @param fillData the rate data to be populated
   */
  /* Unused in 4.22 */
  protected applySlippageToRateData(
    slippage: BigNumber,
    order: OrderDataImpl,
    pair: CurrencyPair,
    fillData: RateData,
  ): void {
    let marketSpot: BigNumber = null;

    let minPrecision: number = order.isInvertedOrderFg
      ? Math.max(
          pair.spotInvertDecimalsQty,
          pair.markupSptInvDecimals,
          pair.limitEntrySpotInvertDecimals,
        )
      : Math.max(pair.rateDecimalsQty, pair.markupSptDecimals, pair.limitEntrySpotDecimals);
    let markup: BigNumber = this.getMargin(order, fillData);

    if (!this.getClientBuysBase(order)) {
      marketSpot = fillData.spotMarketRate.minus(slippage);
    } else {
      marketSpot = fillData.spotMarketRate.plus(slippage);
    }

    let currentDecs: number = marketSpot.decimalPlaces();
    if (currentDecs < minPrecision) {
      marketSpot = new BigNumber(marketSpot.toFixed(minPrecision));
    } else {
      marketSpot = new BigNumber(marketSpot.toFixed(currentDecs));
    }

    let clientSpot: BigNumber = null;
    if (!this.getClientBuysBase(order)) {
      clientSpot = new BigNumber(marketSpot).minus(markup);
    } else {
      clientSpot = new BigNumber(marketSpot).plus(markup);
    }

    currentDecs = clientSpot.decimalPlaces();
    if (currentDecs < minPrecision) {
      clientSpot = new BigNumber(clientSpot.toFixed(minPrecision));
    } else {
      clientSpot = new BigNumber(clientSpot.toFixed(currentDecs));
    }

    let forwardPrecision: number = this.getDecimals(order, pair);

    if (fillData.forwardClientPoints != null) {
      fillData.clientAllIn = new BigNumber(
        new BigNumber(clientSpot)
          .plus(this.convertPipsToPoints(order, pair, fillData.forwardClientPoints))
          .toFixed(forwardPrecision),
      );
    } else {
      fillData.clientAllIn = clientSpot;
    }

    if (fillData.forwardMarketPoints != null) {
      fillData.marketAllIn = new BigNumber(
        new BigNumber(marketSpot)
          .plus(this.convertPipsToPoints(order, pair, fillData.forwardMarketPoints))
          .toFixed(forwardPrecision),
      );
    } else {
      fillData.marketAllIn = marketSpot;
    }

    fillData.spotMarketRate = marketSpot;
    fillData.spotClientRate = clientSpot;
  }

  /**
   * Get the margin from the rate data obtained from a lock for fill request
   * @param order the order to calculate the markup for
   * @param fillData the rate data
   * @returns the markup or 0 if fill data has insufficient data to calculate it
   */
  /* Unused in 4.22 */
  protected getMargin(order: OrderDataImpl, fillData: RateData): BigNumber {
    let clientBuysBase: boolean = this.getClientBuysBase(order);
    let margin: BigNumber = null;

    if (
      fillData &&
      fillData.clientAllIn instanceof BigNumber &&
      fillData.marketAllIn instanceof BigNumber
    ) {
      if (clientBuysBase) {
        margin = fillData.clientAllIn.minus(fillData.marketAllIn);
      } else {
        margin = fillData.marketAllIn.minus(fillData.clientAllIn);
      }
    } else {
      margin = new BigNumber(0);
    }

    return margin;
  }

  /**
   * Helper method to determine if client buys base on the order.
   * @param order the order to consider
   * @returns true if client buys base in dealt form
   */
  /* Unused in 4.22 */
  protected getClientBuysBase(order: OrderDataImpl): boolean {
    let clientBuysBase: boolean =
      (order.baseCode == order.fixedCode && order.clientIntent == OrderIntent.BUY) ||
      (order.clientIntent == OrderIntent.SELL && order.nonBaseCode == order.fixedCode);

    if (order.isInvertedOrderFg === true) {
      clientBuysBase = !clientBuysBase;
    }

    return clientBuysBase;
  }

  /**
   * Helper function to convert pips to points
   *
   * @param order the order
   * @param pair currency pair for the order
   * @param val the value in pips
   * @returns
   */
  /* Unused in 4.22 */
  convertPipsToPoints(order: OrderDataImpl, pair: CurrencyPairImpl, val: BigNumber): BigNumber {
    let decs: number = 4;
    let pips: number = 4;
    let fwd: boolean = order.product === Product.FWD || order.product === Product.NDF;

    if (fwd) {
      if (order.isInvertedOrderFg) {
        decs = Math.max(
          pair.fwdInvertDecimalsQty,
          pair.markupSptInvDecimals,
          pair.limitEntrySpotInvertDecimals,
        );
        pips = pair.fwdInvertPipsQty;
      } else {
        decs = Math.max(
          pair.fwdRateDecimalsQty,
          pair.markupSptDecimals,
          pair.limitEntrySpotDecimals,
        );
        pips = pair.fwdPipsQty;
      }
    } else {
      if (order.isInvertedOrderFg) {
        decs = Math.max(
          pair.spotInvertDecimalsQty,
          pair.markupSptInvDecimals,
          pair.limitEntrySpotInvertDecimals,
        );
        pips = pair.spotInvertDecimalsQty;
      } else {
        decs = Math.max(pair.rateDecimalsQty, pair.markupSptDecimals, pair.limitEntrySpotDecimals);
        pips = pair.rateDecimalsQty;
      }
    }

    let pipsToPoints: BigNumber = new BigNumber("10").pow(-pips);

    return new BigNumber(new BigNumber(val).times(pipsToPoints).toFixed(decs));
  }

  /* Unused in 4.22 */
  protected getPips(order: OrderDataImpl, pair: CurrencyPairImpl): number {
    let pips: number = 4;
    let fwd: boolean = order.product === Product.FWD || order.product === Product.NDF;

    if (fwd) {
      if (order.isInvertedOrderFg) {
        pips = pair.fwdInvertPipsQty;
      } else {
        pips = pair.fwdPipsQty;
      }
    } else {
      if (order.isInvertedOrderFg) {
        pips = pair.spotInvertDecimalsQty;
      } else {
        pips = pair.rateDecimalsQty;
      }
    }

    return pips;
  }

  /* Unused in 4.22 */
  protected getDecimals(order: OrderDataImpl, pair: CurrencyPairImpl): number {
    let decs: number = 4;
    let fwd: boolean = order.product === Product.FWD || order.product === Product.NDF;

    if (fwd) {
      if (order.isInvertedOrderFg) {
        decs = Math.max(
          pair.fwdInvertDecimalsQty,
          pair.markupSptInvDecimals,
          pair.limitEntrySpotInvertDecimals,
        );
      } else {
        decs = Math.max(
          pair.fwdRateDecimalsQty,
          pair.markupSptDecimals,
          pair.limitEntrySpotDecimals,
        );
      }
    } else {
      if (order.isInvertedOrderFg) {
        decs = Math.max(
          pair.spotInvertDecimalsQty,
          pair.markupSptInvDecimals,
          pair.limitEntrySpotInvertDecimals,
        );
      } else {
        decs = Math.max(pair.rateDecimalsQty, pair.markupSptDecimals, pair.limitEntrySpotDecimals);
      }
    }

    return decs;
  }
}
